import { LoadingConfig } from './../../../models/_core/loading-config';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { LoadingController, AlertController, ToastController } from '@ionic/angular';
import { AlertOptions } from '@ionic/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AppError, ApiError } from 'src/app/models/_core/app-error';

/**
 * ID: bh-notifications-service
 * Name: BH Notifications Service
 * Description: Service used for managing notifications, alert messaging, and errors.
 * Version: 5
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2021-07-13 - MW - v2: Implemented dev warning and alert colors
 * 2021-10-11 - MW - v3: Suppressed 401 pop-up alerts
 * 2022-06-08 - MW - v4: Added messaging to global loading spinner
 * 2023-09-26 - MW - v5: Updated for Ionic 7 compatibility
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  env = environment;
  isLoading = false;
  loadingConfig: LoadingConfig = { isLoading: false, message: '' };
  isLoadingBehaviorSubject: BehaviorSubject<LoadingConfig> = new BehaviorSubject(this.loadingConfig);
  loadingElem: HTMLIonLoadingElement = null;
  errorList: AppError[] = [];
  dismissLoadingTimeout = null;
  isErrorShowing = false;

  constructor(
    private loadingController: LoadingController,
    private alertController: AlertController,
    private toastController: ToastController,
    private router: Router
  ) {
  }

  /***
   * Display loading spinner
   */
  async startLoading(message: string = '') {
    if (!this.isLoading) {
      this.isLoading = true;
      this.loadingConfig.isLoading = true;
      this.loadingConfig.message = message;
      this.isLoadingBehaviorSubject.next(this.loadingConfig);
    }

  }

  /***
   * Hide loading spinner
   */
  stopLoading() {
    if (this.isLoading) {
      if (this.dismissLoadingTimeout) {
        clearTimeout(this.dismissLoadingTimeout);
      }
      this.dismissLoadingTimeout = setTimeout(async () => {
        this.isLoading = false;
        this.loadingConfig.isLoading = false;
        this.loadingConfig.message = '';
        this.isLoadingBehaviorSubject.next(this.loadingConfig);
      }, 500);
    }
  }

  /**
   * Displays alert message
   *
   * @param header Header text of the alert message
   * @param message subHeader or body text of the alert message
   * @param dismissAfter Milliseconds to dismiss this alert after
   */
  async showAlert(header: string, message?: string, alertType?: 'information' | 'warning' | 'danger' | 'success', dismissAfter?: number) {
    const options: AlertOptions = {
      header,
      message,
      cssClass: 'wide-alert ' + alertType,
      buttons: [
        {
          text: 'Okay',
          cssClass: 'primary'
        }
      ]
    };

    const alert = await this.alertController.create(options);

    await alert.present();
    if (dismissAfter) {
      setTimeout(() => {
        alert.dismiss();
      }, dismissAfter);
    }
  }

  /**
   * Displays toast message at the top of screen
   *
   * @param msg Message to display in toast
   * @param dismissAfter Milliseconds to dismiss the toast
   */
  async showToast(msg: string, dismissAfter: number = 3000) {
    const toast = await this.toastController.create({
      message: msg,
      duration: dismissAfter,
      position: 'top',
      buttons: [
        {
          text: 'Okay',
          role: 'cancel',
          handler: () => {
          }
        }
      ]
    });

    return await toast.present();
  }

  showDevWarning() {
    this.showAlert(this.env.appDescription + ' DEV Version',
      'You\'re using the non-production DEVELOPMENT version of ' + this.env.appDescription + '.' +
      'This version is used for testing and development of new features and bug fixes. ' +
      '<br><br>Please use the PRODUCTION version for actual work (if available).', 'warning');

  }

  /**
   * Handles errors and user notifications.  Sends error contents to console, parses error for toast, displays error toast.
   *
   * @param err  Send error of any format for parsing (httpErrorResponse, string, etc.)
   * @param userFriendlyText (optional) Define user-friendly error text.
   * Leave out or empty to display error object's message; Send 'skip_toast' to suppress error toast.
   */
  async handleError(err: HttpErrorResponse | string, action: string, parameters: any = null, userFriendlyText: string = '') {
    let header = '';
    let msg = '';
    let showError = true;

    // Console log error/response obj and remove outstanding loading modal
    console.error(err);
    this.stopLoading();

    const headerPrefix = 'Unexpected Error Encountered';
    let msgPrefix = '';

    // Parse error message
    if (typeof err === 'string') {
      header = headerPrefix;
      msg = msgPrefix + err + `. (Action: ${action})`;
    } else if (err.error) {
      header = headerPrefix;
      msg = msgPrefix + err.error.message + `. (Action: ${action})`;
    } else if (err.message) {
      header = headerPrefix;
      msg = msgPrefix + err.message +  + `. (Action: ${action})`;
    } else {
      header = headerPrefix;
      msg = 'An unexpected error occurred.'  + `. (Action: ${action})`;
    }

    // Log Error
    if (typeof err === 'string') {
      const customErr: ApiError = {
        errorName: 'NonDescriptError',
        requestId: null,
        statusCode: -1,
        message: msg,
        path: this.router.url,
        timestamp: new Date().toISOString()
      };
      this.errorList.push(customErr);
    } else {
      this.errorList.push(err.error);
    }

    // Swap out error user-friendly text
    if (userFriendlyText && userFriendlyText.trim().length > 0) {
      msg = userFriendlyText.trim();
    }

    // Check for error exceptions
    if (msg) {
      switch (msg.trim().toLowerCase()) {
        case 'no_alert':
        case 'cordova_not_available':
        case 'plugin_not_installed':
        case 'key [_ss_un] not found.':
          showError = false;
      }
    }

    // Show error pop-up
    if (showError && !this.isErrorShowing && !this.is401Error(err)) {
      this.isErrorShowing = true;
      this.showError(header, msg);
    }
  }

  is401Error(error): boolean {
    if (error instanceof HttpErrorResponse) {
      const msg = error.message;
      if (error.status === 401) {
        return true;
      }
    }
    return false;
  }

  async showError(header, msg) {
    const alert = await this.alertController.create({
      header,
      message: msg,
      backdropDismiss: false,
      cssClass: 'wide-alert error',
      buttons: [
        {
          text: 'Okay',
          role: 'cancel',
          cssClass: 'primary',
          handler: () => {
            this.isErrorShowing = false;
            // console.log('Dismiss clicked');
          }
        }
      ]
    });

    await alert.present();

  }

}
